import {useCallback, useRef, useEffect} from 'react'

/**
 * Manages the display canvas for the remove-bg editor.
 * Composites the original image with the mask using Canvas 2D API.
 *
 * Architecture:
 * - maskCanvas (offscreen): binary mask (white=keep, black=remove)
 * - displayCanvas (visible): original image composited with mask
 */
export function useRemoveBgCanvas({canvasRef, zoom, pan}) {
    const originalImageRef = useRef(null)
    const maskCanvasRef = useRef(null)
    const animFrameRef = useRef(null)
    const compCanvasRef = useRef(null)

    /**
     * Load an image from a URL and return it as an HTMLImageElement.
     */
    const loadImage = useCallback((src) => {
        return new Promise((resolve, reject) => {
            const img = new Image()
            img.crossOrigin = 'anonymous'
            img.onload = () => resolve(img)
            img.onerror = reject
            img.src = src
        })
    }, [])

    /**
     * Initialize the mask canvas from the ML-removed background blob.
     * Extracts the alpha channel as the mask.
     */
    const initMaskFromBlob = useCallback(
        async (originalSrc, removedBgBlob) => {
            const blobUrl = URL.createObjectURL(removedBgBlob)
            try {
                const [origImg, removedImg] = await Promise.all([loadImage(originalSrc), loadImage(blobUrl)])

                originalImageRef.current = origImg
                const w = origImg.naturalWidth
                const h = origImg.naturalHeight

                // Create mask canvas
                const mask = document.createElement('canvas')
                mask.width = w
                mask.height = h
                const maskCtx = mask.getContext('2d')

                // Draw the removed-bg image to extract its alpha channel
                const tempCanvas = document.createElement('canvas')
                tempCanvas.width = w
                tempCanvas.height = h
                const tempCtx = tempCanvas.getContext('2d')
                tempCtx.drawImage(removedImg, 0, 0, w, h)
                const removedData = tempCtx.getImageData(0, 0, w, h)

                // Build mask: white where alpha > 0, black where alpha = 0
                const maskData = maskCtx.createImageData(w, h)
                for (let i = 0; i < removedData.data.length; i += 4) {
                    const alpha = removedData.data[i + 3]
                    const val = alpha > 0 ? 255 : 0
                    maskData.data[i] = 255 // R
                    maskData.data[i + 1] = 255 // G
                    maskData.data[i + 2] = 255 // B
                    maskData.data[i + 3] = val // A: 255=keep, 0=remove
                }
                maskCtx.putImageData(maskData, 0, 0)
                maskCanvasRef.current = mask

                return maskCtx.getImageData(0, 0, w, h)
            } finally {
                URL.revokeObjectURL(blobUrl)
            }
        },
        [loadImage],
    )

    /**
     * Initialize with a fully opaque mask (keep everything = show original image).
     * Used for immediate editor init without waiting for ML processing.
     */
    const initFullOpaqueMask = useCallback(
        async (src) => {
            const origImg = await loadImage(src)
            originalImageRef.current = origImg
            const w = origImg.naturalWidth
            const h = origImg.naturalHeight

            const mask = document.createElement('canvas')
            mask.width = w
            mask.height = h
            const maskCtx = mask.getContext('2d')

            // Fill entirely white + opaque = keep everything
            maskCtx.fillStyle = 'rgba(255,255,255,1)'
            maskCtx.fillRect(0, 0, w, h)

            maskCanvasRef.current = mask
            return maskCtx.getImageData(0, 0, w, h)
        },
        [loadImage],
    )

    /**
     * Render the composited result onto the display canvas.
     * Includes performance marks for profiling (visible in DevTools Performance tab).
     */
    const render = useCallback(() => {
        const canvas = canvasRef?.current
        const origImg = originalImageRef.current
        const mask = maskCanvasRef.current
        if (!canvas || !origImg || !mask) return

        if (typeof performance !== 'undefined' && performance.mark) {
            performance.mark('removebg-render-start')
        }

        const ctx = canvas.getContext('2d')
        const dpr = window.devicePixelRatio || 1
        const displayW = canvas.clientWidth
        const displayH = canvas.clientHeight

        // Set canvas resolution
        canvas.width = displayW * dpr
        canvas.height = displayH * dpr

        ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
        ctx.clearRect(0, 0, displayW, displayH)

        // Draw checkerboard background in the image area
        ctx.save()
        ctx.translate(pan.x, pan.y)
        ctx.scale(zoom, zoom)

        const imgW = origImg.naturalWidth
        const imgH = origImg.naturalHeight

        // Draw checkerboard behind the image
        drawCheckerboard(ctx, 0, 0, imgW, imgH, 10)

        // Composite: draw original, then apply mask with destination-in
        // Use a cached offscreen canvas for compositing (avoid recreating each frame)
        if (!compCanvasRef.current || compCanvasRef.current.width !== imgW || compCanvasRef.current.height !== imgH) {
            compCanvasRef.current = document.createElement('canvas')
            compCanvasRef.current.width = imgW
            compCanvasRef.current.height = imgH
        }
        const compCanvas = compCanvasRef.current
        const compCtx = compCanvas.getContext('2d')
        compCtx.clearRect(0, 0, imgW, imgH)

        compCtx.drawImage(origImg, 0, 0)
        compCtx.globalCompositeOperation = 'destination-in'
        compCtx.drawImage(mask, 0, 0)
        compCtx.globalCompositeOperation = 'source-over'

        ctx.drawImage(compCanvas, 0, 0)
        ctx.restore()

        if (typeof performance !== 'undefined' && performance.mark) {
            performance.mark('removebg-render-end')
            performance.measure('removebg-render', 'removebg-render-start', 'removebg-render-end')
        }
    }, [canvasRef, zoom, pan])

    /**
     * Schedule a render on the next animation frame.
     */
    const scheduleRender = useCallback(() => {
        if (animFrameRef.current) {
            cancelAnimationFrame(animFrameRef.current)
        }
        animFrameRef.current = requestAnimationFrame(render)
    }, [render])

    // Auto-render when zoom/pan changes
    useEffect(() => {
        scheduleRender()
        return () => {
            if (animFrameRef.current) {
                cancelAnimationFrame(animFrameRef.current)
            }
        }
    }, [scheduleRender])

    /**
     * Get the mask canvas for brush operations.
     */
    const getMaskCanvas = useCallback(() => maskCanvasRef.current, [])

    /**
     * Get the mask context for brush painting.
     */
    const getMaskContext = useCallback(() => {
        return maskCanvasRef.current?.getContext('2d') ?? null
    }, [])

    /**
     * Get mask ImageData for history snapshots.
     */
    const getMaskImageData = useCallback(() => {
        const mask = maskCanvasRef.current
        if (!mask) return null
        return mask.getContext('2d').getImageData(0, 0, mask.width, mask.height)
    }, [])

    /**
     * Restore mask from an ImageData snapshot (undo/redo).
     */
    const restoreMask = useCallback(
        (imageData) => {
            const mask = maskCanvasRef.current
            if (!mask || !imageData) return
            mask.getContext('2d').putImageData(imageData, 0, 0)
            scheduleRender()
        },
        [scheduleRender],
    )

    /**
     * Export the final composited result as a Blob at original resolution.
     */
    const exportBlob = useCallback(() => {
        return new Promise((resolve, reject) => {
            const origImg = originalImageRef.current
            const mask = maskCanvasRef.current
            if (!origImg || !mask) {
                reject(new Error('No image or mask available'))
                return
            }

            const w = origImg.naturalWidth
            const h = origImg.naturalHeight
            const exportCanvas = document.createElement('canvas')
            exportCanvas.width = w
            exportCanvas.height = h
            const ctx = exportCanvas.getContext('2d')

            ctx.drawImage(origImg, 0, 0)
            ctx.globalCompositeOperation = 'destination-in'
            ctx.drawImage(mask, 0, 0)

            exportCanvas.toBlob((blob) => {
                if (blob) resolve(blob)
                else reject(new Error('Failed to export canvas'))
            }, 'image/png')
        })
    }, [])

    /**
     * Convert client coordinates to image coordinates (for brush).
     */
    const clientToImage = useCallback(
        (clientX, clientY) => {
            const canvas = canvasRef?.current
            if (!canvas) return null
            const rect = canvas.getBoundingClientRect()
            const x = (clientX - rect.left - pan.x) / zoom
            const y = (clientY - rect.top - pan.y) / zoom
            return {x, y}
        },
        [canvasRef, zoom, pan],
    )

    return {
        initMaskFromBlob,
        initFullOpaqueMask,
        render: scheduleRender,
        getMaskCanvas,
        getMaskContext,
        getMaskImageData,
        restoreMask,
        exportBlob,
        clientToImage,
        originalImageRef,
        maskCanvasRef,
    }
}

function drawCheckerboard(ctx, x, y, w, h, size) {
    ctx.save()
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(x, y, w, h)
    ctx.fillStyle = '#d3d3d3'
    for (let row = 0; row < Math.ceil(h / size); row++) {
        for (let col = 0; col < Math.ceil(w / size); col++) {
            if ((row + col) % 2 === 1) {
                ctx.fillRect(x + col * size, y + row * size, size, size)
            }
        }
    }
    ctx.restore()
}
